/**
 * AddBookModal — search discovery → shelf selection, two-step flow.
 * Search is real Open Library data (via useBookQuickSearch, the same
 * debounced/cached boundary hook Explore's search bar uses) rather than a
 * static mock catalog, so anything a reader can actually add to their
 * shelf is a real, findable book.
 */

import React, { useState } from 'react';
import { Search, Plus, ArrowLeft, BookX } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useBookQuickSearch } from '@/hooks/useBookSearch';
import { toShelfBook } from '@/lib/bookModel';
import BookCoverArt from '@/components/books/BookCoverArt';
import { useBookshelf } from '../context/BookshelfContext';

const SHELF_STATUS_MAP = {
  shelf_tbr: 'tbr',
  shelf_reading: 'reading',
  shelf_finished: 'finished',
};

function ResultsSkeleton() {
  return (
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i}>
          <div className="aspect-[2/3] animate-pulse rounded-r-md rounded-l-[4px] bg-line/60" />
          <div className="mt-1.5 h-3 w-4/5 animate-pulse rounded bg-line/60" />
          <div className="mt-1 h-2.5 w-3/5 animate-pulse rounded bg-line/40" />
        </div>
      ))}
    </div>
  );
}

function SearchStep({ onSelect }) {
  const [query, setQuery] = useState('');
  const { books, isLoading, isError } = useBookQuickSearch(query);
  const hasQuery = query.trim().length > 0;

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">Add Books to Your Bookshelf</h2>
      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search for a book or author…"
          className="w-full rounded-full border border-line bg-cream py-2.5 pl-10 pr-4 text-sm text-ink outline-none transition-colors focus:border-coral"
        />
      </div>

      <div className="mt-5 max-h-[46vh] overflow-y-auto pb-1">
        {!hasQuery && (
          <p className="py-10 text-center text-sm text-subtle">Start typing to search real books from Open Library.</p>
        )}

        {hasQuery && isLoading && <ResultsSkeleton />}

        {hasQuery && !isLoading && isError && (
          <p className="flex flex-col items-center gap-2 py-10 text-center text-sm text-subtle">
            <BookX className="h-6 w-6 text-subtle" /> Couldn&rsquo;t reach the book catalog. Try again in a moment.
          </p>
        )}

        {hasQuery && !isLoading && !isError && books.length === 0 && (
          <p className="py-10 text-center text-sm text-subtle">No books found. Try another search.</p>
        )}

        {hasQuery && !isLoading && !isError && books.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {books.map(book => (
              <button key={book.id} type="button" onClick={() => onSelect(book)} className="group text-left">
                <div className="relative">
                  <BookCoverArt
                    book={book}
                   
                    className="aspect-[2/3] shadow-card transition-transform duration-300 ease-soft group-hover:-translate-y-1"
                  />
                  <span className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-[#FFF7F0]/90 text-coral opacity-0 shadow-soft transition-opacity group-hover:opacity-100">
                    <Plus className="h-3.5 w-3.5" />
                  </span>
                </div>
                <p className="mt-1.5 line-clamp-2 text-xs font-medium text-ink">{book.title}</p>
                {book.authors?.[0] && <p className="truncate text-[11px] text-subtle">{book.authors[0]}</p>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ShelfStep({ book, shelves, onBack, onConfirm }) {
  const [selectedShelf, setSelectedShelf] = useState('shelf_tbr');
  const [notes, setNotes] = useState('');
  const author = book.authors?.[0] ?? 'Unknown Author';

  return (
    <div>
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs font-medium text-subtle hover:text-coral">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to search
      </button>

      <h2 className="mt-3 font-display text-xl font-semibold text-ink">Add to Bookshelf</h2>

      <div className="mt-4 flex items-center gap-3 rounded-xl bg-cream p-3">
        <BookCoverArt book={book} className="h-16 w-11 shrink-0 shadow-card" />
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-semibold text-ink">{book.title}</p>
          <p className="truncate text-xs text-subtle">{author}</p>
        </div>
      </div>

      <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-subtle">Add to</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {shelves.map(shelf => (
          <button
            key={shelf.id}
            type="button"
            onClick={() => setSelectedShelf(shelf.id)}
            className={cx(
              'flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors',
              selectedShelf === shelf.id ? 'border-coral bg-coral/10 text-coral' : 'border-line text-ink hover:border-coral/40'
            )}
          >
            {shelf.emoji && <span>{shelf.emoji}</span>} {shelf.name}
          </button>
        ))}
      </div>

      <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-subtle">Notes (optional)</p>
      <Textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Recommended by a friend, looks great…"
        className="mt-2 rounded-xl border-line bg-cream text-sm"
      />

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="secondary" icon={false} onClick={onBack}>Cancel</Button>
        <Button variant="primary" icon={false} onClick={() => onConfirm(selectedShelf, notes)}>Add to Shelf</Button>
      </div>
    </div>
  );
}

function ModalBody({ onClose }) {
  const { shelves, books, addBook } = useBookshelf();
  const [step, setStep] = useState(1);
  const [selectedBook, setSelectedBook] = useState(null);

  const reset = () => { setStep(1); setSelectedBook(null); };

  const handleConfirm = async (shelfId, notes) => {
    if (books.some(b => b.title.toLowerCase() === selectedBook.title.toLowerCase())) {
      toast(`“${selectedBook.title}” is already on your bookshelf.`);
      reset();
      onClose();
      return;
    }

    const shelf = shelves.find(s => s.id === shelfId);
    const newBook = {
      ...toShelfBook(selectedBook, shelfId),
      status: SHELF_STATUS_MAP[shelfId] ?? 'tbr',
      customShelfIds: shelf?.type === 'custom' ? [shelfId] : [],
      notes: notes || undefined,
    };

    try {
      await addBook(newBook, shelfId);
      toast(`Added “${newBook.title}” to ${shelf?.name ?? 'your shelf'}`);
      reset();
      onClose();
    } catch (error) {
      toast(error.message || `Could not add “${newBook.title}” to your shelf.`);
    }
  };

  if (step === 1) {
    return <SearchStep onSelect={(book) => { setSelectedBook(book); setStep(2); }} />;
  }

  return <ShelfStep book={selectedBook} shelves={shelves} onBack={reset} onConfirm={handleConfirm} />;
}

export default function AddBookModal({ isOpen, onClose }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[85vh] w-[92vw] max-w-xl overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Add a Book to Your Bookshelf</DialogTitle>
          <ModalBody onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[90vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Add a Book to Your Bookshelf</DrawerTitle>
        <ModalBody onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
